/**
 * Columns Tab - Expense column CRUD for marketplace settings
 */

import React from 'react';
import { Plus, Trash2, GripVertical, Eye, EyeOff, Columns } from 'lucide-react';
import type { CustomMarketplace, CustomColumn } from '../types';
import { generateId } from '../../../utils/database';

interface ColumnsTabProps {
  selectedMarketplace: CustomMarketplace;
  setSelectedMarketplace: (mp: CustomMarketplace) => void;
}

const ColumnsTab: React.FC<ColumnsTabProps> = ({
  selectedMarketplace,
  setSelectedMarketplace,
}) => {
  const handleAddColumn = () => {
    const newColumn: CustomColumn = {
      id: generateId(),
      name: '',
      key: '',
      type: 'percent',
      category: 'custom',
      scope: 'category',
      visible: true,
      editable: true,
      defaultValue: 0,
      order: selectedMarketplace.columns.length + 1,
    };

    setSelectedMarketplace({
      ...selectedMarketplace,
      columns: [...selectedMarketplace.columns, newColumn],
    });
  };

  const handleUpdateColumn = (index: number, field: keyof CustomColumn, value: string | number | boolean) => {
    const updatedColumns = [...selectedMarketplace.columns];
    updatedColumns[index] = { ...updatedColumns[index], [field]: value };

    // Auto-generate key from name
    if (field === 'name' && typeof value === 'string') {
      updatedColumns[index].key = value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    }

    setSelectedMarketplace({
      ...selectedMarketplace,
      columns: updatedColumns,
    });
  };

  const handleDeleteColumn = (index: number) => {
    setSelectedMarketplace({
      ...selectedMarketplace,
      columns: selectedMarketplace.columns.filter((_, i) => i !== index),
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-medium text-slate-700">Gider S&#xFC;tunlar&#x131;</h3>
        <button
          onClick={handleAddColumn}
          className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg"
        >
          <Plus className="w-4 h-4" />
          S&#xFC;tun Ekle
        </button>
      </div>

      <div className="space-y-2">
        {selectedMarketplace.columns.map((column, index) => (
          <div
            key={column.id}
            className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg"
          >
            <GripVertical className="w-4 h-4 text-slate-400 cursor-move" />
            <span className="text-xs text-slate-400 w-6">{index + 1}</span>
            <input
              type="text"
              value={column.name}
              onChange={(e) => handleUpdateColumn(index, 'name', e.target.value)}
              className="flex-1 px-3 py-1 border border-slate-200 rounded text-sm"
              placeholder="S&#xFC;tun Ad&#x131;"
            />
            <select
              value={column.type}
              onChange={(e) => handleUpdateColumn(index, 'type', e.target.value)}
              className="w-24 px-2 py-1 border border-slate-200 rounded text-sm"
            >
              <option value="percent">Y&#xFC;zde (%)</option>
              <option value="fixed">Sabit</option>
              <option value="currency">Para</option>
            </select>
            <input
              type="number"
              value={column.defaultValue || 0}
              onChange={(e) => handleUpdateColumn(index, 'defaultValue', parseFloat(e.target.value) || 0)}
              className="w-20 px-2 py-1 border border-slate-200 rounded text-sm text-right"
              step="0.1"
            />
            <button
              onClick={() => handleUpdateColumn(index, 'visible', !column.visible)}
              className={`p-1.5 rounded ${column.visible ? 'text-indigo-600 bg-indigo-50' : 'text-slate-400 hover:bg-slate-100'}`}
              title={column.visible ? 'G\u00F6r\u00FCn\u00FCr' : 'Gizli'}
            >
              {column.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            </button>
            <button
              onClick={() => handleDeleteColumn(index)}
              className="p-1 text-red-500 hover:bg-red-50 rounded"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      {selectedMarketplace.columns.length === 0 && (
        <div className="text-center py-8 text-slate-500">
          <Columns className="w-12 h-12 mx-auto mb-2 text-slate-300" />
          <p>Hen&#xFC;z s&#xFC;tun eklenmemi&#x15F;</p>
        </div>
      )}
    </div>
  );
};

export default ColumnsTab;
